import React, { useRef } from "react";
import {
  Grid,
  Input,
  makeStyles,
  styled,
  IconButton,
  withTheme,
  Typography,
  Select,
} from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import esLocale from "date-fns/locale/es";
import { ArrowDownSelectIcon, CalendarIcon } from "../icons";

export default function TextField(props) {
  const {
    children,
    type,
    name,
    error,
    onBlur,
    onChange,
    helperText,
    disabled,
    placeholder,
    containerStyle,
    value,
    onClickIcon,
    select,
    minDate,
    maxDate,
    iconComponent: IconComponent,
    style,
    multiline,
    rows,
    profile,
    fromProfile,
    inputComponent,
    id,
    onFocus,
    onKeyPress,
    ...rest
  } = props;
  const classes = useStyles({
    select,
    multiline,
    profile,
    fromProfile,
    ...props,
  })();

  const ref = useRef();
  return (
    <Grid container style={{ ...containerStyle, position: "relative" }}>
      <Grid
        container
        alignItems="center"
        item
        xs={12}
        sm={12}
        style={{ ...style, position: "relative" }}
      >
        {type === "date" ? (
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
            <DatePicker
              clearable
              value={value}
              fullWidth
              className={classes.datePicker}
              placeholder={placeholder}
              inputProps={{ className: classes.dateInput }}
              onChange={(date) => onChange(date)}
              minDate={minDate ? minDate : null}
              maxDate={maxDate ? maxDate : null}
              onBlur={onBlur ? () => onBlur() : () => {}}
              format="yyyy"
            />
            <CalendarIcon style={{ position: "absolute", right: 18 }} />
          </MuiPickersUtilsProvider>
        ) : !select ? (
          <StyledInput
            className={classes.input}
            fullWidth
            disableUnderline
            profile={profile}
            value={value}
            multiline={multiline}
            onKeyPress={onKeyPress ? (e) => onKeyPress(e) : () => {}}
            rows={rows}
            onFocus={onFocus}
            placeholder={placeholder}
            inputProps={{ color: "#192A7F" }}
            type={type ? type : "text"}
            name={name}
            onChange={(e) => onChange(e)}
            onBlur={onBlur ? () => onBlur() : () => {}}
            error={error}
            disabled={disabled}
            ref={ref}
            id={id}
            inputComponent={inputComponent}
            {...rest}
          />
        ) : (
          <Select
            value={value}
            select
            disabled={disabled}
            name={name}
            fullWidth
            fromprofile={fromProfile}
            IconComponent={() => <ArrowDownSelectIcon />}
            onChange={(e) => {
              onChange(e);
            }}
            input={<StyledInput disableUnderline />}
            error={error}
            onBlur={onBlur ? () => onBlur() : () => {}}
          >
            {children}
          </Select>
        )}
        {IconComponent && (
          <IconButton onClick={onClickIcon} style={{ position: "absolute", right: 0, zIndex: 1 }}>
            <IconComponent />
          </IconButton>
        )}
        {error && (
          <Typography className={classes.error} variant="caption">
            {helperText}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}

const useStyles = ({ select, profile, fromProfile, ...props }) =>
  makeStyles((theme) => ({
    input: {
      "& ::placeholder": {
        color: theme.palette.primary.main,
        opacity: 1 /* Firefox */,
        fontWeight: props.fontWeight ? props.fontWeight : "400",
      },
      ": -ms-input-placeholder": {
        color: theme.palette.primary.main,
        fontWeight: props.fontWeight ? props.fontWeight : "400",
      },
      ":: -ms-input-placeholder": {
        /* Microsoft Edge */ color: theme.palette.primary.main,
        fontWeight: props.fontWeight ? props.fontWeight : "400",
      },
    },
    error: {
      fontSize: "0.7rem",
      // position: "absolute",
      // left: 3,
      // bottom: -20,
      marginTop: 5,
      marginLeft: 3,
      color: "red",
      whiteSpace: "pre-line",
    },
    dateInput: {
      color: theme.palette.secondary.main,
      "&::-webkit-input-placeholder": {
        color: theme.palette.secondary.main /*Change the placeholder color*/,
        opacity: 1 /*Change the opacity between 0 and 1*/,
      },
    },
    datePicker: {
      height: props.profile || props.fromProfile ? "56px!important" : 43,
      borderRadius: 25,
      boxShadow: "0px 4px 10px rgba(198, 198, 198, 0.5)",
      "& .MuiInput-underline": {
        height: "100%",
        "& input": {
          padding: "0 0 0 18px",
          fontSize: 12,
        },
        "& .MuiInputAdornment-root": {
          "& svg": {
            color: theme.palette.secondary.main,
          },
        },
      },
      "& .MuiInput-underline:before, .MuiInput-underline:after": {
        border: 0,
      },
      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
        border: 0,
      },
    },
  }));

export const StyledInput = styled(withTheme(Input))((props) => {
  return {
    background: "#FFF",
    // color: props.select
    //   ? `${props.theme.palette.secondary.main}!important`
    //   : `${props.theme.palette.primary.main}!important`,
    color: `${props.theme.palette.primary.main}!important`,
    height: props.multiline ? "unset" : props.profile ? "56px!important" : 43,
    padding: props.multiline ? 18 : props.profile ? "0 13px" : "0 18px",
    boxSizing: props.profile ? "content-box" : "unset",
    fontWeight: !props.profile ? "unset" : "500",
    border: props.profile
      ? "unset"
      : props.error
      ? "1pt solid red !important"
      : "1pt solid #B2B2B2",
    boxShadow: props.profile
      ? "0px 4px 10px rgba(198, 198, 198, 0.5)"
      : "unset",
    borderRadius: props.profile ? 22 : 25,
    fontSize: props.profile ? "1.125rem" : 12,
    [props.theme.breakpoints.down("xs")]: {
      height: props.multiline ? "unset" : props.profile ? "56px!important" : 43,
    },
    "& svg": {
      right: 20,
      color: props.theme.palette.secondary.main,
    },
    "&:hover": {
      border: props.profile
        ? "unset"
        : props.error
        ? "1pt solid red"
        : "1pt solid #B2B2B2",
    },
    "&:after": {
      border: 0,
    },
    "&:before": {
      border: 0,
    },
    "&:focus": {
      border: props.profile
        ? "unset"
        : props.error
        ? "1pt solid red"
        : "1pt solid #B2B2B2",
    },
    "&:active": {
      border: props.profile
        ? "unset"
        : props.error
        ? "1pt solid red"
        : "1pt solid #B2B2B2",
    },
  };
});
